import { FC, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  ArrowForwardIos as ArrowForwardIosIcon,
  ReportProblemOutlined as ReportProblemOutlinedIcon,
} from "@material-ui/icons";

import { formatCurrencyAmount } from "lib/format";
import theme, { Theme } from "theme";

import AssetAdministrationModal from "../AssetAdministrationModal";
import { SellerAssetAdministrationItem } from "../index";

const useStyles = makeStyles<Theme, { isSmDown: boolean }>(() => ({
  cardActionArea: {
    color: theme.custom.palette.purple.main,
    height: "100%",
    minHeight: "82px",
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    "& .nextArrow": {
      fontSize: "0.75rem",
      transition: "transform 200ms",
    },
    "&:hover": {
      cursor: "pointer",
      borderColor: theme.custom.palette.purple.main,
      backgroundColor: theme.custom.palette.purple.lightest,
      "& .nextArrow": {
        transform: "translateX(4px)",
      },
      "& .MuiCardActions-root": {
        opacity: 1,
      },
    },
  },
  cardContainer: {
    color: theme.palette.text.primary, // resets color, the parent action area should have color blue for hover effect
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  cardContent: {
    width: "100%",
    padding: 0,
    "&:last-child": {
      padding: 0,
    },
  },
  cardHeader: {
    padding: 0,
    paddingBottom: theme.spacing(1),
    textAlign: "left",
  },
  cardActions: {
    color: theme.custom.palette.purple.main,
    fontSize: "0.875rem",
    letterSpacing: "1.2px",
    opacity: 0.5,
    padding: 0,
    width: "100%",
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
}));

export interface AdministrationCardProps {
  sellerAssetAdministrationItem: SellerAssetAdministrationItem;
}

const AdministrationCard: FC<AdministrationCardProps> = ({
  sellerAssetAdministrationItem,
}) => {
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles({ isSmDown });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isAdvance =
    sellerAssetAdministrationItem.purchased_term == "fixed_return";
  const cardText = isAdvance ? "Advance Balance" : "Royalties Received";

  const cardAmount = isAdvance
    ? formatCurrencyAmount(
        sellerAssetAdministrationItem.currency,
        sellerAssetAdministrationItem.advance_balance,
      )
    : formatCurrencyAmount(
        sellerAssetAdministrationItem.currency,
        sellerAssetAdministrationItem.royalties_received,
      );

  return (
    <>
      <Card
        onClick={() => setIsModalOpen(true)}
        className={`${classes.cardActionArea}`}
      >
        <CardActionArea className={classes.cardContainer}>
          <CardContent>
            <CardHeader
              className={classes.cardHeader}
              title={
                <Box display="flex">
                  <Box mr={1}>{sellerAssetAdministrationItem.deal_title}</Box>
                  {!!sellerAssetAdministrationItem.hasPaymentStatusUpdate && (
                    <Box>
                      <ReportProblemOutlinedIcon
                        fontSize="small"
                        className={classes.warningIcon}
                      />
                    </Box>
                  )}
                </Box>
              }
              titleTypographyProps={{
                style: {
                  fontWeight: theme.typography.fontWeightMedium,
                  fontSize: "1.125rem",
                },
              }}
            />
            <Box
              display="flex"
              justifyContent="flex-start"
              flexDirection="column"
              width="100%"
            >
              <Typography variant="h5" component="div">
                <Box fontWeight="fontWeightLight">{cardAmount}</Box>
              </Typography>
              <Typography variant="body2" component="div">
                <Box fontWeight={theme.typography.fontWeightMedium}>
                  {cardText}
                </Box>
              </Typography>
            </Box>
          </CardContent>
          <CardActions className={classes.cardActions}>
            <Box
              width="100%"
              display="flex"
              justifyContent="end"
              alignItems="center"
            >
              View Statements
              <ArrowForwardIosIcon fontSize="small" className="nextArrow" />
            </Box>
          </CardActions>
        </CardActionArea>
      </Card>
      {isModalOpen && (
        <AssetAdministrationModal
          onClose={() => setIsModalOpen(false)}
          sellerAssetAdministrationItem={sellerAssetAdministrationItem}
        />
      )}
    </>
  );
};

export default AdministrationCard;
